// src/pages/Section7.jsx

import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import KeyFeatures from "../components/KeyFeatures";

import heroImg from "../assets/abuthr.png";
import aboutImg from "../assets/aboutoursolution.png";
import HomeHowItWorksSection from "../components/home/HowItWorksSection";
import poiImg from "../assets/poi.png";
import manageRoutesImg from "../assets/manageroutes.png";
import powerfulReportImg from "../assets/powerfullreport.jpg";
import whyTruckImg from "../assets/whytruck.png";

import {
  CheckCircle2,
  CircleCheckBig,
  Leaf,
  PieChart,
  UsersRound,
  Smartphone,
  Map,
  Edit3,
  Truck,
  Clock,
  AlertTriangle,
  RotateCcw,
  Power,
  Gauge,
} from "lucide-react";

const containerClass = "mx-auto w-full max-w-[1160px] px-4 sm:px-6 lg:px-8";

const features = [
  {
    title: "Real-Time Tracking",
    text: "Monitor the real-time location and status of your waste collection vehicles and trash bins on licensed GIS maps. Stay informed about fleet movements throughout the collection process.",
  },
  {
    title: "Geofencing Technology",
    text: "Create virtual boundaries for stoppage points to efficiently pick up trash bins. Get automated alerts when vehicles enter or exit designated zones for better operation management.",
  },
  {
    title: "Attendance Management",
    text: "Track real-time attendance of remote employees through biometric machines deployed at each ward. Ensure accountability and productivity of your workforce.",
  },
  {
    title: "Trip Tracking",
    text: "Monitor real-time trips of particular fleet vehicles deployed over a period of 30 days or as customized. Analyze route efficiency and optimize collection patterns.",
  },
  {
    title: "Complaint Redressal",
    text: "Implement an intuitive complaint redressal system with 24/7 operational support to address and resolve issues promptly and effectively.",
  },
  {
    title: "Vehicle Scheduling",
    text: "Schedule vehicles in particular wards or zones with the ability to notify changes as needed. Optimize fleet deployment for maximum efficiency.",
  },
  {
    title: "Visual Documentation",
    text: "Upload real-time pictures of trash bins before and after cleaning through the supervisor mobile application. Maintain visual records for quality assurance.",
  },
  {
    title: "Analytical Reports",
    text: "Access comprehensive analytical reports including Vehicle-Zone Reports, Total Working Hours, Run Reports, Driving Behavior, Fuel Reports, Load Reports, and more.",
  },
];

const keyFeatureItems = features.map(({ title, text }) => ({
  title,
  desc: text,
}));

const benefits = [
  {
    title: "Enhanced Efficiency",
    text: "Optimize routes and collection schedules to reduce fuel consumption, vehicle wear, and operational costs while improving service delivery.",
    Icon: CheckCircle2,
  },
  {
    title: "Improved Accountability",
    text: "Track workforce activities and vehicle movements in real-time to ensure compliance with schedules and routes for better service reliability.",
    Icon: CircleCheckBig,
  },
  {
    title: "Environmental Impact",
    text: "Reduce carbon footprint through optimized routes and efficient waste collection, contributing to a cleaner and healthier environment.",
    Icon: Leaf,
  },
  {
    title: "Data-Driven Decisions",
    text: "Leverage comprehensive analytics and reports to make informed decisions about resource allocation and operational improvements.",
    Icon: PieChart,
  },
  {
    title: "Improved Citizen Satisfaction",
    text: "Enhance service quality and responsiveness to complaints, leading to higher citizen satisfaction and trust in waste management services.",
    Icon: UsersRound,
  },
  {
    title: "Real-Time Visibility",
    text: "Access actionable alerts and insights delivered in real-time on a simple, comprehensive web dashboard, iOS, and Android applications.",
    Icon: Smartphone,
  },
];

const section7HowItWorksSteps = [
  {
    step: "01",
    title: "Installation",
    text: "Deploy RFID tags in garbage bins and trucks, along with biometric machines for workforce tracking. Our team handles the complete setup process, ensuring all hardware components are properly installed and configured.",
  },
  {
    step: "02",
    title: "Configuration",
    text: "Set up geofencing for collection points and create schedules for vehicles in different zones. Configure the system according to your specific operational requirements and waste collection patterns.",
  },
  {
    step: "03",
    title: "Monitoring",
    text: "Track real-time movements of vehicles and workforce through the web and mobile applications. Monitor all aspects of your waste collection operations from a centralized dashboard.",
  },
  {
    step: "04",
    title: "Analysis",
    text: "Generate detailed reports and analyze data to optimize operations and resource allocation. Identify patterns, trends, and areas for improvement in your waste management processes.",
  },
  {
    step: "05",
    title: "Optimization",
    text: "Implement improvements based on data insights to continuously enhance efficiency and service quality. Our team provides ongoing support to help you optimize your waste management operations.",
  },
];


function Reveal({ children, className = "", direction = "up", delay = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const current = ref.current;
    if (!current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(current);
        }
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -40px 0px",
      }
    );

    observer.observe(current);

    return () => observer.disconnect();
  }, []);

  const hiddenPosition = {
    up: "translate-y-10",
    left: "-translate-x-10",
    right: "translate-x-10",
    zoom: "scale-95",
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${delay} ${visible
        ? "translate-x-0 translate-y-0 scale-100 opacity-100"
        : `${hiddenPosition[direction] || hiddenPosition.up} opacity-0`
        } ${className}`}
    >
      {children}
    </div>
  );
}



function HeroSection() {
  return (
    <section className="relative mx-auto w-full max-w-[calc(100%-12px)] overflow-hidden rounded-b-[58px] bg-black sm:max-w-[calc(100%-24px)] lg:max-w-[calc(100%-32px)]">
      <Navbar />

      <div className="relative min-h-[520px] overflow-hidden sm:min-h-[560px] lg:min-h-[620px]">
        <img
          src={heroImg}
          alt="Smart solid waste management solution"
          className="absolute inset-0 h-full w-full object-cover"
        />

        <div className="absolute inset-0 bg-black/70" />

        <div className="relative z-10 flex min-h-[520px] items-center justify-center px-4 pt-24 text-center text-white sm:min-h-[560px] lg:min-h-[620px]">
          <Reveal>
            <div className="max-w-[900px]">
              <h1 className="relative inline-block text-[34px] font-bold leading-tight tracking-[-0.04em] sm:text-[48px] lg:text-[58px]">
                Smart Solid Waste Management Solution
                <span className="absolute -bottom-4 left-1/2 h-[3px] w-[72%] -translate-x-1/2 rounded-full bg-[#ef7d00]" />
              </h1>

              <p className="mx-auto mt-9 max-w-[690px] text-[14px] leading-relaxed text-white/75 sm:text-[15px]">
                Transforming waste management with IoT technology for efficient
                monitoring, tracking, and optimization of your waste collection
                operations.
              </p>

              <div className="mt-8 flex flex-wrap justify-center gap-5">
                <a
                  href="#features"
                  className="rounded-md bg-[#ef7d00] px-10 py-3.5 text-[15px] font-medium text-white transition duration-300 hover:-translate-y-1 hover:bg-[#d96f00]"
                >
                  Explore Features
                </a>

                <a
                  href="/contact"
                  className="rounded-md border border-white px-10 py-3.5 text-[15px] font-medium text-white transition duration-300 hover:-translate-y-1 hover:bg-white hover:text-black"
                >
                  Request demo
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function AboutSolutionSection() {
  return (
    <section className="bg-white py-14 sm:py-16 lg:py-20">
      <div className={containerClass}>
        <Reveal>
          <h2 className="text-center text-3xl font-bold tracking-[-0.04em] text-black sm:text-4xl lg:text-[42px]">
            About Our Solution
          </h2>
          <p className="mx-auto mt-4 max-w-[760px] text-center text-sm leading-relaxed text-gray-600">
            Revolutionizing waste management through advanced technology and
            data-driven insights
          </p>
        </Reveal>

        <div className="mt-12 grid items-center gap-12 lg:grid-cols-2">
          <Reveal direction="left">
            <div className="max-w-[520px]">
              <h2 className="text-[28px] font-bold leading-tight tracking-[-0.04em] text-black sm:text-[34px]">
                Intelligent Waste Management
              </h2>

              <p className="mt-5 text-[15px] leading-relaxed text-gray-700">
                FleetRobo, with its excellence in Vehicle Tracking Systems,
                offers municipal authorities a robust Smart Waste Management
                tracking Solution. We aid authorities and 3rd parties with smart
                waste management tracking and monitoring solutions based on
                RFID/IoT technology.
                <br />
                <br />
                Our comprehensive system enables better monitoring of the
                workforce and fleet deployed right from the filling of trash
                bins, picking them up, and transporting them to proper disposal
                sites and landfills.
              </p>

              <a
                href="#features"
                className="mt-10 inline-flex rounded-md bg-[#ef7d00] px-10 py-3.5 text-[15px] font-medium text-white transition duration-300 hover:-translate-y-1 hover:bg-[#d96f00]"
              >
                Discover Features
              </a>
            </div>
          </Reveal>

          <Reveal direction="right">
            <div className="h-[300px] overflow-hidden rounded-xl bg-gray-200 sm:h-[360px]">
              <img
                src={aboutImg}
                alt="Smart waste management analytics"
                className="h-full w-full object-cover transition duration-700 hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  return (
    <section className="bg-white py-16 sm:py-20 lg:py-24">
      <div className={containerClass}>
        <Reveal>
          <div className="text-center">
            <h2 className="text-3xl font-bold leading-tight tracking-[-0.04em] text-black sm:text-4xl lg:text-[42px]">
              Benefits
            </h2>

            <div className="mx-auto mt-4 h-[3px] w-16 rounded-full bg-[#ef7d00]" />

            <p className="mx-auto mt-5 max-w-[620px] text-sm leading-relaxed text-gray-600 sm:text-[15px]">
              Discover how our smart waste management solution can transform
              your operations
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-x-12 gap-y-20 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-20 lg:gap-y-24">
          {benefits.map((item, index) => {
            const Icon = item.Icon;

            return (
              <Reveal key={item.title} delay={index < 3 ? "" : "delay-100"}>
                <article className="mx-auto max-w-[320px] text-center">
                  <Icon
                    className="mx-auto h-7 w-7 text-[#ef7d00]"
                    strokeWidth={2.4}
                  />

                  <h3 className="mt-7 text-[21px] font-bold leading-tight tracking-[-0.04em] text-black sm:text-[23px]">
                    {item.title}
                  </h3>

                  <p className="mx-auto mt-5 max-w-[300px] text-[15px] leading-[1.75] text-gray-700">
                    {item.text}
                  </p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}


function PoiRouteTrackingSection() {
  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-16 lg:py-20">
      <div className="absolute left-0 top-20 h-72 w-72 rounded-full bg-[#ef7d00]/10 blur-3xl" />
      <div className="absolute bottom-10 right-0 h-72 w-72 rounded-full bg-[#00176f]/5 blur-3xl" />

      <div className="relative mx-auto w-full max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <Reveal direction="left">
            <div className="max-w-[620px]">


              <h2 className="text-[34px] font-bold leading-tight tracking-[-0.025em] text-black sm:text-[40px] lg:text-[45px]">
                POI (Point of Interest) Based Route Tracking
              </h2>

              <p className="mt-7 max-w-[620px] text-[15px] font-normal leading-relaxed text-gray-600 sm:text-base">
                With Veyonta, every waste collection route is divided into small
                checkpoints called POIs (Points of Interest). We place these
                POIs at every 100 meters along the route.
              </p>

              <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_18px_50px_rgba(0,0,0,0.06)] transition-all duration-500 hover:-translate-y-1 hover:border-[#ef7d00]/40 hover:shadow-[0_24px_70px_rgba(239,125,0,0.16)]">
                <h3 className="text-2xl font-bold tracking-[-0.015em] text-black">
                  How it works?
                </h3>

                <p className="mt-4 text-sm font-medium leading-relaxed text-gray-600 sm:text-[15px]">
                  When a garbage truck moves along the route, the system checks
                  whether the truck has passed each POI. If the driver misses
                  even one location, for example skips a street or turns early,
                  the system immediately detects it and generates a report
                  showing which POIs were missed with exact map locations.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal direction="right">
            <div className="relative">
              <div className="absolute -right-4 top-8 h-64 w-64 rounded-full bg-[#ef7d00]/10 blur-3xl" />

              <div className="relative mx-auto max-w-[620px]">
                <img
                  src={poiImg}
                  alt="POI based route tracking with missed pickup alert"
                  className="w-full object-contain drop-shadow-[0_24px_42px_rgba(0,0,0,0.12)] transition-transform duration-700 hover:scale-[1.03]"
                />


              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}


function ManageRoutesSection() {
  const routePoints = [
    {
      text: "Create new collection routes based on area or zone",
      Icon: Map,
    },
    {
      text: "View and edit routes anytime as per changes in locality",
      Icon: Edit3,
    },
    {
      text: "Assign specific tippers (trucks) to each route",
      Icon: Truck,
    },
    {
      text: "Add trip timings to monitor when a vehicle should start and finish",
      Icon: Clock,
    },
    {
      text: "See which trucks are not assigned or not reporting",
      Icon: AlertTriangle,
    },
    {
      text: "Replay completed routes to check how the driver actually performed",
      Icon: RotateCcw,
    },
  ];

  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-16 lg:py-20">
      <div className="absolute left-0 top-16 h-72 w-72 rounded-full bg-[#ef7d00]/10 blur-3xl" />
      <div className="absolute bottom-10 right-0 h-72 w-72 rounded-full bg-[#00176f]/5 blur-3xl" />

      <div className="relative mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto max-w-[880px] text-center">
            <h2 className="text-3xl font-bold leading-tight tracking-[-0.025em] text-black sm:text-4xl lg:text-[42px]">
              Manage Routes & Vehicles Easily
            </h2>

            <p className="mx-auto mt-5 max-w-[760px] text-sm font-normal leading-relaxed text-gray-600 sm:text-[15px]">
              Plan, assign, and monitor waste collection routes with complete
              visibility over vehicles, schedules, and field operations.
              Optimize collection efficiency, reduce delays, and ensure every
              route is completed as planned.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal direction="left">
            <div className="overflow-hidden rounded-md bg-white">
              <img
                src={manageRoutesImg}
                alt="Manage collection routes dashboard map"
                className="w-full object-contain transition-transform duration-700 hover:scale-[1.02]"
              />
            </div>
          </Reveal>

          <Reveal direction="right">
            <div className="space-y-6">
              {routePoints.map((point) => {
                const Icon = point.Icon;

                return (
                  <div
                    key={point.text}
                    className="group grid grid-cols-[52px_1fr] items-center gap-5"
                  >
                    <div className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-[#FDEDDC] text-[#ef7d00] transition-all duration-500 group-hover:scale-110 group-hover:bg-[#ef7d00] group-hover:text-white group-hover:shadow-[0_14px_30px_rgba(239,125,0,0.25)]">
                      <Icon className="h-5 w-5" strokeWidth={2.3} />
                    </div>

                    <p className="text-sm font-medium leading-relaxed text-gray-700 transition-colors duration-300 group-hover:text-black sm:text-[15px]">
                      {point.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}


function PowerfulReportsSection() {
  const reportPoints = [
    {
      text: "Ignition Reports: Know when the truck was turned on and off",
      Icon: Power,
    },
    {
      text: "Distance & Speed Reports: See how far each truck traveled and if any overspeeding happened",
      Icon: Gauge,
    },
    {
      text: "Idling Reports: Detect where and for how long the vehicle was left running unnecessarily",
      Icon: Clock,
    },
    {
      text: "Over Stoppage Reports: Identify unplanned or extended stops that waste time and fuel",
      Icon: AlertTriangle,
    },
  ];

  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-16 lg:py-20">
      <div className="absolute left-0 top-16 h-72 w-72 rounded-full bg-[#ef7d00]/10 blur-3xl" />
      <div className="absolute bottom-10 right-0 h-72 w-72 rounded-full bg-[#00176f]/5 blur-3xl" />

      <div className="relative mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr]">
          <Reveal direction="left">
            <div>
              <h2 className="text-3xl font-bold leading-tight tracking-[-0.025em] text-black sm:text-4xl lg:text-[42px]">
                Powerful Reports for Better Decisions
              </h2>

              <p className="mt-6 max-w-[760px] text-sm font-normal leading-relaxed text-gray-600 sm:text-[15px]">
                Transform collection data into actionable insights with detailed
                reports on vehicle activity, route performance, fuel usage,
                idling time, and operational efficiency to support smarter
                decision-making.
              </p>

              <div className="mt-10 space-y-6">
                {reportPoints.map((point) => {
                  const Icon = point.Icon;

                  return (
                    <div
                      key={point.text}
                      className="group grid grid-cols-[52px_1fr] items-start gap-5"
                    >
                      <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#FDEDDC] text-[#ef7d00] transition-all duration-500 group-hover:scale-110 group-hover:bg-[#ef7d00] group-hover:text-white group-hover:shadow-[0_14px_30px_rgba(239,125,0,0.25)]">
                        <Icon className="h-5 w-5" strokeWidth={2.3} />
                      </div>

                      <p className="pt-2 text-sm font-medium leading-relaxed text-gray-700 transition-colors duration-300 group-hover:text-black sm:text-[15px]">
                        {point.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal direction="right">
            <div className="relative">
              <div className="absolute -right-4 top-10 h-64 w-64 rounded-full bg-[#ef7d00]/10 blur-3xl" />

              <div className="relative mx-auto max-w-[620px] overflow-hidden rounded-md bg-white">
                <img
                  src={powerfulReportImg}
                  alt="Powerful reports dashboard map"
                  className="w-full object-contain transition-transform duration-700 hover:scale-[1.02]"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}


function WhyOrganizationsChooseSection() {
  const choosePoints = [
    {
      text: "POI Tracking ensures no area is missed during waste collection",
      Icon: Map,
    },
    {
      text: "Live GPS monitoring helps track your fleet from anywhere",
      Icon: Smartphone,
    },
    {
      text: "Reduce fuel consumption & improve driver discipline",
      Icon: Gauge,
    },
    {
      text: "Auto-generated reports support safety, compliance, and audits",
      Icon: PieChart,
    },
    {
      text: "Scalable system — whether you manage 5 trucks or 500, Blackbox grows with you",
      Icon: Truck,
    },
    {
      text: "Trusted by municipalities and contractors across India",
      Icon: CircleCheckBig,
    },
  ];

  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-16 lg:py-20">
      <div className="absolute left-0 top-14 h-72 w-72 rounded-full bg-[#ef7d00]/10 blur-3xl" />
      <div className="absolute bottom-10 right-0 h-72 w-72 rounded-full bg-[#00176f]/5 blur-3xl" />

      <div className="relative mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto max-w-[860px] text-center">
            <h2 className="text-3xl font-bold leading-tight tracking-[-0.025em] text-black sm:text-4xl lg:text-[42px]">
              Why Organizations Choose Veyonta
            </h2>

            <p className="mx-auto mt-5 max-w-[780px] text-sm font-normal leading-relaxed text-gray-600 sm:text-[15px]">
              Empower your waste management operations with intelligent fleet
              monitoring, route optimization, real-time tracking, and data-driven
              insights designed to improve efficiency, reduce costs, and ensure
              reliable service delivery.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal direction="left">
            <div className="relative mx-auto max-w-[620px]">
              <img
                src={whyTruckImg}
                alt="Why organizations choose Veyonta waste truck features"
                className="w-full object-contain transition-transform duration-700 hover:scale-[1.03]"
              />
            </div>
          </Reveal>

          <Reveal direction="right">
            <div className="space-y-6">
              {choosePoints.map((point) => {
                const Icon = point.Icon;

                return (
                  <div
                    key={point.text}
                    className="group grid grid-cols-[52px_1fr] items-center gap-5"
                  >
                    <div className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-[#FDEDDC] text-[#ef7d00] transition-all duration-500 group-hover:scale-110 group-hover:bg-[#ef7d00] group-hover:text-white group-hover:shadow-[0_14px_30px_rgba(239,125,0,0.25)]">
                      <Icon className="h-5 w-5" strokeWidth={2.3} />
                    </div>

                    <p className="text-sm font-medium leading-relaxed text-gray-700 transition-colors duration-300 group-hover:text-black sm:text-[15px]">
                      {point.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="bg-white py-14 sm:py-16 lg:py-20">
      <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-10 xl:px-14">
        <Reveal direction="zoom">
          <div className="w-full rounded-xl bg-gradient-to-r from-[#ef7d00] via-[#b66325] to-[#00176f] px-8 py-12 sm:px-14 lg:px-20 lg:py-16">
            <h2 className="text-3xl font-bold tracking-[-0.05em] text-black sm:text-4xl lg:text-[42px]">
              Get Started in Minutes
            </h2>

            <p className="mt-5 max-w-[820px] text-sm leading-relaxed text-black">
              Experience the power of automation with FleetRobo® AIS-140 ICAT
              Certified Devices
            </p>

            <div className="mt-8 flex flex-wrap gap-5">
              <a
                href="#contact"
                className="rounded-md border border-white px-12 py-3.5 text-[15px] font-medium text-white transition duration-300 hover:-translate-y-1 hover:bg-white hover:text-black"
              >
                Contact us
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Section7() {
  return (
    <>
      <main className="bg-white font-['Poppins'] text-black">
        <HeroSection />
        <AboutSolutionSection />

        <div id="features">
          <KeyFeatures title="Key Features" features={keyFeatureItems} />
        </div>

        <BenefitsSection />
        <PoiRouteTrackingSection />
        <ManageRoutesSection />
        <PowerfulReportsSection />
        <WhyOrganizationsChooseSection />

        <HomeHowItWorksSection
          title="How It Works"
          subtitle="Understanding our streamlined approach to smart waste management."
          steps={section7HowItWorksSteps}
        />
        <CtaSection />
      </main>

      <Footer />
    </>
  );
}